import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { EducationInclusion } from '../../interfaces/application-package';

/**
 * Education cockpit: one list split into "In the CV" and "Set aside", plus a
 * per-entry description control (show/hide + edit). The show toggle is only
 * offered when the entry actually has description text, so a description is
 * never silently lost. Emits intent; the parent owns HTTP + refresh.
 *
 * Description TEXT editing has two routes and is offered in BOTH flag states,
 * because it predates #1420 and its endpoint
 * (POST .../education/{id}/description) is not flag-gated: with manualEdit on
 * it delegates to the shared bst-field-editor in the parent workspace
 * (editDescription); with manualEdit off it keeps this panel's own inline
 * textarea, which posts {show: true, text} through descriptionChange exactly
 * as it did before #1420.
 */
@Component({
  selector: 'bst-education-panel',
  templateUrl: './education-panel.component.html',
  styleUrls: ['./education-panel.component.scss'],
  standalone: false,
})
export class EducationPanelComponent implements OnChanges {
  @Input() public education: EducationInclusion[] = [];
  @Input() public busy = false;
  /** Set once the consultant's edit window is closed; blocks set-aside/restore. */
  @Input() public disabled = false;
  /** Manual editing is on for this package (CV_MANUAL_EDIT_ENABLED and the
   * edit window is open): routes description text through the shared field
   * editor instead of this panel's inline one. Neither the show/hide toggle
   * nor text editing itself is behind the flag. */
  @Input() public manualEdit = false;

  @Output() public setAside = new EventEmitter<number>();
  @Output() public restore = new EventEmitter<number>();
  @Output() public descriptionChange = new EventEmitter<{ eduId: number; show: boolean; text?: string }>();
  @Output() public editDescription = new EventEmitter<{ eduId: number; label: string; current: string }>();
  @Output() public editField = new EventEmitter<
    | { field: 'education_text'; educationId: number; edField: 'degree' | 'role' | 'employer'; label: string; current: string }
    | { field: 'period'; section: 'education'; id: number; label: string; start: string | null; end: string | null }
  >();

  public editingId: number | null = null;
  public draft = '';

  public get included(): EducationInclusion[] { return this.education.filter(e => e.state === 'included'); }
  public get setAsideList(): EducationInclusion[] { return this.education.filter(e => e.state === 'set_aside'); }

  public ngOnChanges(): void {
    if (this.editingId !== null && !this.included.some(e => e.edu_id === this.editingId)) {
      this.editingId = null;
      this.draft = '';
    }
  }

  public toggleShow(e: EducationInclusion): void {
    this.descriptionChange.emit({ eduId: e.edu_id, show: !e.show_description });
  }

  public startEdit(e: EducationInclusion): void {
    this.editingId = e.edu_id;
    this.draft = e.description;
  }

  public saveEdit(e: EducationInclusion): void {
    this.descriptionChange.emit({ eduId: e.edu_id, show: true, text: this.draft });
    this.editingId = null;
  }

  public cancelEdit(): void {
    this.editingId = null;
  }
}
